import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { RequireAuth } from "./auth/RequireAuth";
import { LoginPage } from "./pages/LoginPage";
import { HomePage } from "./pages/HomePage";
import { LanguageSwitcher } from "./components/LanguageSwitcher";

const AdminPage = lazy(() => import("./pages/admin/AdminPage").then((m) => ({ default: m.AdminPage })));
const DashboardTab = lazy(() => import("./pages/admin/DashboardTab").then((m) => ({ default: m.DashboardTab })));
const EquipmentTab = lazy(() => import("./pages/admin/EquipmentTab").then((m) => ({ default: m.EquipmentTab })));
const ProcessesTab = lazy(() => import("./pages/admin/ProcessesTab").then((m) => ({ default: m.ProcessesTab })));
const SpecsTab = lazy(() => import("./pages/admin/SpecsTab").then((m) => ({ default: m.SpecsTab })));
const ToolsTab = lazy(() => import("./pages/admin/ToolsTab").then((m) => ({ default: m.ToolsTab })));
const CategoriesTab = lazy(() => import("./pages/admin/CategoriesTab").then((m) => ({ default: m.CategoriesTab })));
const UsersTab = lazy(() => import("./pages/admin/UsersTab").then((m) => ({ default: m.UsersTab })));
const NewReportPage = lazy(() => import("./pages/reports/NewReportPage").then((m) => ({ default: m.NewReportPage })));
const ReportEntryPage = lazy(() => import("./pages/reports/ReportEntryPage").then((m) => ({ default: m.ReportEntryPage })));
const ReportListPage = lazy(() => import("./pages/reports/ReportListPage").then((m) => ({ default: m.ReportListPage })));

function Loading() {
  return (
    <div className="h-full flex items-center justify-center text-slate-500">…</div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="h-screen flex flex-col bg-slate-950 text-slate-100">
      <header className="flex items-center justify-between px-4 h-12 border-b border-slate-800 shrink-0">
        <button
          onClick={() => navigate("/")}
          className="font-semibold tracking-tight hover:text-sky-300 transition duration-200 ease-out"
        >
          {t("app.title")}
        </button>
        <div className="flex items-center gap-3">
          <LanguageSwitcher compact />
          {user && (
            <>
              <span className="text-xs text-slate-400">{user.name}</span>
              <button
                onClick={() => void logout()}
                className="text-xs text-slate-400 hover:text-slate-100 transition duration-200 ease-out"
              >
                {t("common.logout")}
              </button>
            </>
          )}
        </div>
      </header>
      <main className="flex-1 overflow-hidden">{children}</main>
    </div>
  );
}

export function App() {
  return (
    <AuthProvider>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/"
            element={
              <RequireAuth>
                <Shell>
                  <HomePage />
                </Shell>
              </RequireAuth>
            }
          />
          <Route
            path="/admin"
            element={
              <RequireAuth roles={["admin", "manager"]}>
                <Shell>
                  <AdminPage />
                </Shell>
              </RequireAuth>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<DashboardTab />} />
            <Route path="equipment" element={<EquipmentTab />} />
            <Route path="processes" element={<ProcessesTab />} />
            <Route path="specs" element={<SpecsTab />} />
            <Route path="tools" element={<ToolsTab />} />
            <Route path="categories" element={<CategoriesTab />} />
            <Route path="users" element={<UsersTab />} />
          </Route>
          <Route
            path="/reports"
            element={
              <RequireAuth>
                <Shell>
                  <ReportListPage />
                </Shell>
              </RequireAuth>
            }
          />
          <Route
            path="/reports/new"
            element={
              <RequireAuth>
                <Shell>
                  <NewReportPage />
                </Shell>
              </RequireAuth>
            }
          />
          <Route
            path="/reports/:equipmentId/:date"
            element={
              <RequireAuth>
                <Shell>
                  <ReportEntryPage />
                </Shell>
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}
